import React from 'react';
import { Link } from 'react-router-dom';
import {
  MapPinIcon,
  BriefcaseIcon,
  CalendarDaysIcon,
  BuildingOfficeIcon,
  ArrowRightIcon,
} from '@heroicons/react/24/outline';

// Importar los iconos para las categorías
import { FaRegHandshake } from 'react-icons/fa';
import { FaPeopleGroup } from 'react-icons/fa6';
import { PiSteeringWheel, PiMoneyWavy } from 'react-icons/pi';
import { BsShopWindow } from 'react-icons/bs';

const JobCard = ({ job }) => {
  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    const [year, month, day] = dateString.split('-');
    return new Date(year, month - 1, day).toLocaleDateString('es-ES', options);
  };

  // Determinar el icono según el área/departamento
  const getCategoryIcon = (area) => {
    switch (area) {
      case 'Ventas':
        return (
          <FaRegHandshake className="h-20 w-20 md:h-28 md:w-28 text-white" />
        );
      case 'Operaciones':
        return (
          <PiSteeringWheel className="h-20 w-20 md:h-28 md:w-28 text-white" />
        );
      case 'Tesorería':
        return <PiMoneyWavy className="h-20 w-20 md:h-28 md:w-28 text-white" />;
      case 'Atracción de Talento':
        return (
          <FaPeopleGroup className="h-20 w-20 md:h-28 md:w-28 text-white" />
        );
      case 'Comercial':
        return (
          <BsShopWindow className="h-20 w-20 md:h-28 md:w-28 text-white" />
        );
      default:
        return (
          <BriefcaseIcon className="h-20 w-20 md:h-28 md:w-28 text-white" />
        );
    }
  };

  // Determinar el color según el área/departamento
  const getCategoryColor = (area) => {
    switch (area) {
      case 'Ventas':
        return {
          bgGradient: 'from-blue-500 to-cyan-500',
          bgLight: 'from-blue-50 to-blue-100',
          text: 'text-blue-600',
          border: 'border-blue-200',
          btn: 'bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600',
        };
      case 'Operaciones':
        return {
          bgGradient: 'from-orange-500 to-amber-500',
          bgLight: 'from-orange-50 to-orange-100',
          text: 'text-orange-600',
          border: 'border-orange-200',
          btn: 'bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600',
        };

      case 'Tesorería':
        return {
          bgGradient: 'from-green-500 to-emerald-500',
          bgLight: 'from-green-50 to-green-100',
          text: 'text-green-600',
          border: 'border-green-200',
          btn: 'bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600',
        };
      case 'Atracción de Talento':
        return {
          bgGradient: 'from-purple-500 to-fuchsia-500',
          bgLight: 'from-purple-50 to-purple-100',
          text: 'text-purple-600',
          border: 'border-purple-200',
          btn: 'bg-gradient-to-r from-purple-500 to-fuchsia-500 hover:from-purple-600 hover:to-fuchsia-600',
        };
      case 'Comercial':
        return {
          bgGradient: 'from-teal-500 to-emerald-500',
          bgLight: 'from-teal-50 to-teal-100',
          text: 'text-teal-600',
          border: 'border-teal-200',
          btn: 'bg-gradient-to-r from-teal-500 to-emerald-500 hover:from-teal-600 hover:to-emerald-600',
        };
      default:
        return {
          bgGradient: 'from-blue-600 to-blue-800',
          bgLight: 'from-gray-50 to-gray-100',
          text: 'text-gray-600',
          border: 'border-gray-200',
          btn: 'bg-gradient-to-r from-blue-600 to-blue-800 hover:from-blue-700 hover:to-blue-900',
        };
    }
  };

  const categoryColor = getCategoryColor(job.area_departamento);

  return (
    <Link
      to={`/vacante/${job.slug}`}
      className="no-underline cursor-pointer block"
    >
      <div className="group bg-white rounded-xl md:rounded-2xl p-4 md:p-6 shadow-lg hover:shadow-2xl transition-all duration-500 border border-gray-100 hover:border-primary-orange/30 hover:translate-y-[-2px] md:hover:translate-y-[-4px] h-full flex flex-col">
        <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4 md:gap-8">
          {/* Left Section */}
          <div className="flex-1">
            <div className="flex flex-col sm:flex-row items-start gap-4 md:gap-6">
              {/* Company Logo */}
              <div className="flex-shrink-0 w-full sm:w-auto flex justify-center sm:justify-start">
                <div
                  className={`w-full h-20 md:w-32 md:h-32 bg-gradient-to-br ${categoryColor.bgGradient} rounded-lg md:rounded-xl flex items-center justify-center p-2 md:p-3 shadow-md group-hover:scale-105 transition-transform duration-300`}
                >
                  {getCategoryIcon(job.area_departamento)}
                </div>
              </div>

              <div className="flex-1 w-full">
                {/* Badges */}
                <div className="flex flex-wrap gap-2 mb-3">
                  <span
                    className={`px-2 md:px-3 py-1 md:py-1.5 bg-gradient-to-r ${categoryColor.bgLight} ${categoryColor.text} text-xs font-semibold rounded-full border ${categoryColor.border}`}
                  >
                    {job.area_departamento}
                  </span>
                  <span className="px-2 md:px-3 py-1 md:py-1.5 bg-primary-orange/10 text-primary-orange text-xs font-semibold rounded-full border border-primary-orange/20">
                    {job.tipo_empleo}
                  </span>
                  <span className="px-2 md:px-3 py-1 md:py-1.5 bg-gray-100 text-gray-700 text-xs font-semibold rounded-full border border-gray-200">
                    {job.horario}
                  </span>
                </div>

                <h3 className="text-lg md:text-xl lg:text-2xl font-bold text-gray-900 mt-1 md:mt-2 group-hover:text-primary-blue transition-colors duration-300 line-clamp-2 leading-tight">
                  {job.titulo_vacante}
                </h3>

                {/* Company and Location */}
                <div className="flex flex-col sm:flex-row sm:flex-wrap items-start sm:items-center gap-3 md:gap-4 mt-3">
                  <div className="flex items-center gap-2 w-full sm:w-auto">
                    <div className="p-1 md:p-1.5 bg-gray-100 rounded-lg">
                      <BuildingOfficeIcon className="h-3.5 w-3.5 md:h-4 md:w-4 text-gray-600" />
                    </div>
                    <span className="text-sm font-medium text-gray-700 truncate">
                      {job.nombre_empresa}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 w-full sm:w-auto">
                    <div className="p-1 md:p-1.5 bg-gray-100 rounded-lg">
                      <MapPinIcon className="h-3.5 w-3.5 md:h-4 md:w-4 text-gray-600" />
                    </div>
                    <span className="text-sm font-medium text-gray-700">
                      {job.localidad}, {job.estado}
                    </span>
                  </div>
                  {/* Salary - Discreet Format */}
                  <div className="p-2 md:p-3 bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg md:rounded-xl border border-gray-200 w-full sm:w-fit font-bold text-sm md:text-base mt-1 sm:mt-0">
                    ${job.salario} {job.moneda} /{' '}
                    {job.tipo_salario.toLowerCase()}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer Section */}
        <div className="mt-4 md:mt-6 pt-4 md:pt-6 border-t border-gray-100">
          <div className="flex flex-row sm:items-center justify-between gap-3 md:gap-0">
            {/* Date */}
            <div className="flex items-center gap-2 text-xs md:text-sm text-gray-500">
              <CalendarDaysIcon className="h-3.5 w-3.5 md:h-4 md:w-4" />
              <span>Publicado: {formatDate(job.fecha_posteo)}</span>
            </div>

            {/* CTA Button */}
            <div className="flex items-center justify-between sm:justify-end gap-4">
              <div className="flex items-center text-primary-orange font-semibold text-sm md:text-base group">
                <span className="group-hover:underline">Ver detalles</span>
                <ArrowRightIcon className="h-3.5 w-3.5 md:h-4 md:w-4 ml-1 md:ml-2 transform group-hover:translate-x-1 transition-transform" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
};

export default JobCard;
